import { useTranslation } from 'react-i18next';
import { ChevronDown, FileText, LayoutGrid } from 'lucide-react';
import { cn } from '../../lib/utils';
import { parseCoils } from './utils';
import { CategoryChip } from '../ui-components/category/CategoryChip';
import FavoriteButton from './FavoriteButton';
import CoilChip from './CoilChip';
import FieldStrengthChip from './FieldStrengthChip';
import { MRIMachinePopulated, NO_LOCATION } from './types';
import { LocationSection, roomPolicies } from './roomOverviewUtils';

interface MachineListTableProps {
    sections: LocationSection[];
    hasLocations: boolean;
    collapsedSections: Set<string>;
    onToggleSection: (key: string) => void;
    favoritedMachineIds: Set<string>;
    onFavoriteChange: (machineId: string, isFavorited: boolean) => void;
    onClick: (machineId: string) => void;
}

const GRID_COLS =
    'grid grid-cols-[32px_minmax(0,1.4fr)_minmax(0,1.4fr)_64px_minmax(0,1fr)_112px] items-center gap-x-4 px-3';

function CountChip({
    icon: Icon,
    count,
    label,
}: {
    icon: typeof LayoutGrid;
    count: number;
    label: string;
}) {
    return (
        <span
            className="inline-flex items-center gap-1 text-xs font-medium tabular-nums text-subtle-foreground"
            title={label}
        >
            <Icon className="h-3 w-3" aria-hidden="true" />
            {count}
        </span>
    );
}

export default function MachineListTable({
    sections,
    hasLocations,
    collapsedSections,
    onToggleSection,
    favoritedMachineIds,
    onFavoriteChange,
    onClick,
}: MachineListTableProps) {
    const { t } = useTranslation();

    const renderRow = (machine: MRIMachinePopulated) => {
        const coils = parseCoils(machine.TRCoils);
        const itemCount = machine.equipment?.length ?? 0;
        const policyCount = roomPolicies(machine).length;
        return (
            <div
                key={machine._id}
                role="button"
                tabIndex={0}
                aria-label={machine.Name}
                onClick={() => onClick(machine._id)}
                onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onClick(machine._id);
                    }
                }}
                className={cn(
                    GRID_COLS,
                    'h-11 cursor-pointer border-b border-subtle transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                )}
            >
                <FavoriteButton
                    machineId={machine._id}
                    machineName={machine.Name}
                    isFavorited={favoritedMachineIds.has(machine._id)}
                    onFavoriteChange={onFavoriteChange}
                />
                <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-sm font-medium">
                        {machine.Name}
                    </span>
                    {machine.category && (
                        <CategoryChip category={machine.category} />
                    )}
                </div>
                <div className="truncate text-sm">
                    {machine.Model}
                    {machine.Manufacturer && (
                        <span className="font-medium text-subtle-foreground">
                            {' · '}
                            {machine.Manufacturer}
                        </span>
                    )}
                </div>
                <div>
                    <FieldStrengthChip fieldStrength={machine.FieldStrength} />
                </div>
                <div
                    className="flex min-w-0 items-center gap-1 overflow-hidden"
                    title={coils.join(', ')}
                >
                    {coils.slice(0, 2).map((coil, i) => (
                        <CoilChip key={i} coil={coil} />
                    ))}
                    {coils.length > 2 && (
                        <span className="shrink-0 text-xs font-medium text-subtle-foreground">
                            +{coils.length - 2}
                        </span>
                    )}
                </div>
                <div className="flex items-center justify-end gap-3">
                    {itemCount > 0 && (
                        <CountChip
                            icon={LayoutGrid}
                            count={itemCount}
                            label={t('roomOverview.machineCard.itemCount', {
                                count: itemCount,
                            })}
                        />
                    )}
                    {policyCount > 0 && (
                        <CountChip
                            icon={FileText}
                            count={policyCount}
                            label={t('roomOverview.machineCard.policyCount', {
                                count: policyCount,
                            })}
                        />
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="overflow-x-auto">
            <div className="min-w-[800px]">
                <div
                    className={cn(
                        GRID_COLS,
                        'h-9 border-b border-subtle text-[11px] font-medium uppercase tracking-wider text-subtle-foreground'
                    )}
                >
                    <span />
                    <span>{t('roomOverview.machineList.room')}</span>
                    <span>{t('roomOverview.machineList.model')}</span>
                    <span>{t('roomOverview.machineList.field')}</span>
                    <span>{t('roomOverview.machineList.coils')}</span>
                    <span className="whitespace-nowrap text-right">
                        {t('roomOverview.machineList.items')}
                    </span>
                </div>
                {!hasLocations &&
                    sections.flatMap((s) => s.machines).map(renderRow)}
                {hasLocations &&
                    sections.map((section) => {
                        const collapsed = collapsedSections.has(section.key);
                        return (
                            <div key={section.key} className="mt-4 first:mt-0">
                                <button
                                    type="button"
                                    onClick={() => onToggleSection(section.key)}
                                    aria-expanded={!collapsed}
                                    className="flex h-10 w-full items-center gap-2 rounded-md bg-muted px-3 text-left"
                                >
                                    <ChevronDown
                                        className={cn(
                                            'h-4 w-4 shrink-0 text-subtle-foreground transition-transform',
                                            collapsed && '-rotate-90'
                                        )}
                                        aria-hidden="true"
                                    />
                                    <span className="text-base font-medium">
                                        {section.key === NO_LOCATION
                                            ? t(
                                                  'roomOverview.roomOverview.sections.other'
                                              )
                                            : section.key}
                                    </span>
                                    <span className="text-xs font-medium text-subtle-foreground">
                                        {t(
                                            'roomOverview.roomOverview.sections.roomCount',
                                            { count: section.machines.length }
                                        )}
                                    </span>
                                </button>
                                {!collapsed && section.machines.map(renderRow)}
                            </div>
                        );
                    })}
            </div>
        </div>
    );
}
